// VideoEmbed — click-to-play facade for a video link in a comment (roadmap #6, rich comments). We NEVER embed
// arbitrary HTML: a comment's text is scanned for links to an ALLOWLIST of providers (YouTube / Vimeo / TED),
// the video id/slug is parsed out, and the embed URL is BUILT from it. Nothing loads until the reader clicks —
// we show the provider's poster (YouTube via a predictable image URL; Vimeo/TED via their public oEmbed, with a
// clean fallback card), then swap in the iframe on click. youtube-nocookie for the actual player.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

export type VideoProvider = 'youtube' | 'vimeo' | 'ted';

export interface VideoRef {
  /** Stable key (provider:id) for React + de-duplication. */
  key: string;
  provider: VideoProvider;
  /** Video id (YouTube/Vimeo) or talk slug (TED). */
  id: string;
  /** The original link the guest pasted (used for the oEmbed lookup). */
  url: string;
}

const PROVIDER_LABEL: Record<VideoProvider, string> = { youtube: 'YouTube', vimeo: 'Vimeo', ted: 'TED' };

// Ordered patterns → provider + capture group for the id/slug. Matched against the raw comment text.
const PATTERNS: Array<{ provider: VideoProvider; re: RegExp }> = [
  { provider: 'youtube', re: /https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:[^\s]*&)?v=([A-Za-z0-9_-]{11})/gi },
  { provider: 'youtube', re: /https?:\/\/youtu\.be\/([A-Za-z0-9_-]{11})/gi },
  { provider: 'youtube', re: /https?:\/\/(?:www\.)?youtube\.com\/(?:embed|shorts|live)\/([A-Za-z0-9_-]{11})/gi },
  { provider: 'vimeo', re: /https?:\/\/(?:www\.)?vimeo\.com\/(?:video\/)?(\d{6,})/gi },
  { provider: 'vimeo', re: /https?:\/\/player\.vimeo\.com\/video\/(\d{6,})/gi },
  { provider: 'ted', re: /https?:\/\/(?:www\.)?ted\.com\/talks\/([A-Za-z0-9_]+)/gi },
];

/** Find the recognized video links in a comment's raw text — unique, in order of appearance. */
export const findVideoEmbeds = (text: string): VideoRef[] => {
  const out: VideoRef[] = [];
  const seen = new Set<string>();
  for (const { provider, re } of PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text || '')) !== null) {
      const id = m[1];
      const key = `${provider}:${id}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ key, provider, id, url: m[0] });
      }
    }
  }
  return out;
};

const embedUrl = (v: VideoRef): string => {
  if (v.provider === 'youtube') {
    return `https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`;
  }
  if (v.provider === 'vimeo') {
    return `https://player.vimeo.com/video/${v.id}?autoplay=1`;
  }
  return `https://embed.ted.com/talks/${v.id}`;
};

const oembedUrl = (v: VideoRef): string | null => {
  if (v.provider === 'vimeo') {
    return `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(v.url)}`;
  }
  if (v.provider === 'ted') {
    return `https://www.ted.com/services/v1/oembed.json?url=${encodeURIComponent(v.url)}`;
  }
  return null; // YouTube uses a predictable image URL, no fetch
};

export function VideoEmbed({ video }: { video: VideoRef }) {
  const [playing, setPlaying] = useState(false);
  const [thumb, setThumb] = useState<string | null>(
    video.provider === 'youtube' ? `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg` : null,
  );
  const cancelled = useRef(false);

  // Fetch a poster for Vimeo/TED via their public oEmbed (no key). Failures fall back to the plain play-card.
  useEffect(() => {
    cancelled.current = false;
    const url = thumb ? null : oembedUrl(video);
    if (url) {
      fetch(url)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!cancelled.current && d && typeof d.thumbnail_url === 'string') {
            setThumb(d.thumbnail_url);
          }
        })
        .catch(() => {
          /* no poster — the fallback card is fine */
        });
    }
    return () => {
      cancelled.current = true;
    };
  }, [video, thumb]);

  const frame = useMemo<CSSProperties>(
    () => ({
      position: 'relative',
      width: '100%',
      maxWidth: 480,
      aspectRatio: '16 / 9',
      borderRadius: 8,
      overflow: 'hidden',
      marginTop: 8,
      border: '1px solid var(--mantine-color-default-border, rgba(0,0,0,0.12))',
      background: '#000',
    }),
    [],
  );

  if (playing) {
    return (
      <div style={frame}>
        <iframe
          // A video (not any embed): a black & white theme greys it like the site's own videos (events-web globals.css).
          className="em-video-frame"
          src={embedUrl(video)}
          title={`${PROVIDER_LABEL[video.provider]} video`}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play ${PROVIDER_LABEL[video.provider]} video`}
      style={{
        ...frame,
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: thumb ? `url(${thumb})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 22,
          paddingLeft: 4,
        }}
      >
        ▶
      </span>
      <span
        style={{
          position: 'absolute',
          left: 8,
          bottom: 6,
          fontSize: 11,
          color: '#fff',
          background: 'rgba(0,0,0,0.55)',
          padding: '1px 6px',
          borderRadius: 4,
        }}
      >
        {PROVIDER_LABEL[video.provider]}
      </span>
    </button>
  );
}
